/**
 * Resize Overlay Component
 *
 * Provides visual resize controls for widgets in edit mode.
 * Shows a grid overlay and size selector for precise resizing.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React, { useState } from 'react';
import { Minus, Plus, Check } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WidgetInstance } from '../types';
import { getWidgetById } from '../widgetRegistry';
import { useDashboard } from '../DashboardContext';

interface ResizeOverlayProps {
  instance: WidgetInstance;
  onClose: () => void;
}

const ResizeOverlay: React.FC<ResizeOverlayProps> = ({ instance, onClose }) => {
  const { t } = useTranslation();
  const { updateWidgetSize } = useDashboard();
  const widgetDef = getWidgetById(instance.widgetId);

  const minWidth = widgetDef?.minWidth || 2;
  const minHeight = widgetDef?.minHeight || 2;
  const maxWidth = widgetDef?.maxWidth || 6;
  const maxHeight = widgetDef?.maxHeight || 4;

  const [width, setWidth] = useState(instance.size.width);
  const [height, setHeight] = useState(instance.size.height);

  const handleApply = () => {
    updateWidgetSize(instance.instanceId, { width, height });
    onClose();
  };

  const adjustWidth = (delta: number) => {
    const newWidth = Math.max(minWidth, Math.min(maxWidth, width + delta));
    setWidth(newWidth);
  };

  const adjustHeight = (delta: number) => {
    const newHeight = Math.max(minHeight, Math.min(maxHeight, height + delta));
    setHeight(newHeight);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-xl p-6 shadow-2xl border border-gray-600"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white mb-4 text-center">
          {t('manager.modules.wmsDashboard.resize.title', {}, 'Endre størrelse')}
        </h3>

        {/* Size Preview Grid */}
        <div className="mb-6 flex justify-center">
          <div
            className="border-2 border-blue-500 rounded-lg bg-blue-500/20 transition-all duration-200"
            style={{
              width: `${width * 50}px`,
              height: `${height * 40}px`,
              minWidth: '100px',
              minHeight: '80px',
            }}
          >
            <div className="w-full h-full flex items-center justify-center text-blue-400 font-medium">
              {width} x {height}
            </div>
          </div>
        </div>

        {/* Width Control */}
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-2 text-center">{t('manager.modules.wmsDashboard.resize.width', {}, 'Bredde')}</label>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => adjustWidth(-1)}
              disabled={width <= minWidth}
              className="w-10 h-10 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
            >
              <Minus className="w-5 h-5" />
            </button>
            <div className="w-16 text-center">
              <span className="text-2xl font-bold text-white">{width}</span>
              <span className="text-sm text-gray-500 ml-1">/ {maxWidth}</span>
            </div>
            <button
              onClick={() => adjustWidth(1)}
              disabled={width >= maxWidth}
              className="w-10 h-10 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Height Control */}
        <div className="mb-6">
          <label className="block text-sm text-gray-400 mb-2 text-center">{t('manager.modules.wmsDashboard.resize.height', {}, 'Høyde')}</label>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => adjustHeight(-1)}
              disabled={height <= minHeight}
              className="w-10 h-10 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
            >
              <Minus className="w-5 h-5" />
            </button>
            <div className="w-16 text-center">
              <span className="text-2xl font-bold text-white">{height}</span>
              <span className="text-sm text-gray-500 ml-1">/ {maxHeight}</span>
            </div>
            <button
              onClick={() => adjustHeight(1)}
              disabled={height >= maxHeight}
              className="w-10 h-10 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-colors"
          >
            {t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" />
            {t('manager.modules.wmsDashboard.resize.apply', {}, 'Bruk')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResizeOverlay;
